import React, { useState, useEffect } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import Image from "next/image";

import { MediaData } from "@demo/types/base";
import { getImageSrc } from "@demo/utils/media";

interface ImageViewerProps {
  media: MediaData;
  mediaIndex: number;
  onImageClick: (e: React.MouseEvent) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  className?: string;
  children?: React.ReactNode;
}

export function ImageViewer({
  media,
  mediaIndex,
  onImageClick,
  onLoadingChange,
  className = "",
  children,
}: ImageViewerProps) {
  const imageSrc = getImageSrc(media);
  const [aspectRatio, setAspectRatio] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!imageSrc) return;

    // Deliberately NOT clearing the previous dimensions here. Doing so renders
    // `<Image>` at 0×0 inside a ratio-less box until the decode resolves, which
    // collapses the frame on every source change — very visible once a carousel
    // can switch assets. Holding the outgoing geometry keeps the frame stable;
    // it is replaced the moment the new image reports its own.
    let cancelled = false;
    const img = new window.Image();

    img.onload = () => {
      if (cancelled) return; // a newer source won the race
      setAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      console.error("Failed to load image for aspect ratio calculation:", imageSrc);
    };

    img.src = imageSrc;

    // Cleanup
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSrc]);

  if (!imageSrc) {
    return (
      <div className={cn("flex h-full w-full items-center justify-center bg-gray-800 text-white", className)}>
        <div className="text-center">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          <p className="mt-2 text-sm">{strings.mediaCarousel.errors.imageFailedToLoad}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full max-h-full w-full flex-col items-center justify-center">
      {/* Container that wraps both image and overlay with same dimensions */}
      <div
        className="relative z-10 h-full max-h-full w-auto max-w-full"
        style={{
          aspectRatio: aspectRatio || undefined,
        }}
      >
        <Image
          alt={formatString(strings.mediaCarousel.mediaAlt, mediaIndex + 1)}
          className="h-full w-auto cursor-pointer object-contain"
          height={imageDimensions?.height || 0}
          priority={mediaIndex === 0}
          src={imageSrc}
          width={imageDimensions?.width || 0}
          onClick={onImageClick}
          onError={(e) => {
            console.error("Image failed to load:", e);
            onLoadingChange?.(false);
          }}
          onLoad={() => {
            onLoadingChange?.(false);
          }}
        />

        {/* Absolute positioned overlay that exactly matches the image */}
        <div
          className="pointer-events-none absolute inset-0 z-50 m-auto flex items-center justify-center"
          style={{
            aspectRatio: aspectRatio || undefined,
          }}
        >
          {/* Re-enable pointer events for children if needed */}
          <div className="pointer-events-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}
